import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Grid,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import Icon from "@mui/material/Icon";
import MyDatePicker from "../../atoms/Common/MyDatePicker";
import LGAutocomplete from "../../atoms/Common/LGAutocomplete.tsx";
import { LGNumber } from "../../atoms/Inputs/LGNumber.tsx";
import { getProviderList } from "../../api/providers";
import { getCurrencyList } from "../../api/common/currencies";
import { getCatalogue } from "../../api/common/catalogues.ts";
import { getScheduledPaymentList } from "../../api/scheduledPayments";
import { getReceiptAcknowledgements } from "../../api/reciptAcknowledgement";
import { getRaPaymentsByReceipt } from "../../api/raPayments";
import { getVarianceReasonList } from "../../api/varianceReasons";
import { createPaymentTx, getPaymentTxById, updatePaymentTx } from "../../api/paymentTx";
import { useSnack } from "../../services/SnakbarProvider";
import { handleError } from "../../services/errorHandler";
import { formatCurrency } from "../../services/valueFormatters";
import dayjs from "dayjs";

const emptyAllocation = () => ({ receipt: null, installments: [], raPaymentId: "", amountApplied: "", varianceReasonId: "" });

export default function PaymentTxForm({ companyId, id, onSubmit }) {
  const { openSnack } = useSnack();
  const isEdit = Boolean(id);

  // Header fields
  const [provider, setProvider] = useState(null);
  const [currency, setCurrency] = useState(null);
  const [exchangeRate, setExchangeRate] = useState("1");
  const [paymentDate, setPaymentDate] = useState(null);
  const [amountPaid, setAmountPaid] = useState("");
  const [sourceBankAccount, setSourceBankAccount] = useState(null);
  const [bankReference, setBankReference] = useState("");
  const [trackingKey, setTrackingKey] = useState("");
  const [scheduledPayment, setScheduledPayment] = useState(null);

  // Allocations (create-only)
  const [allocations, setAllocations] = useState([emptyAllocation()]);
  const [varianceReasons, setVarianceReasons] = useState([]);

  const [loading, setLoading] = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(isEdit);

  useEffect(() => {
    if (!isEdit) return;
    getPaymentTxById(id)
      .then((response) => {
        const tx = response.data;
        setProvider(tx.provider);
        setCurrency(tx.currency);
        setExchangeRate(String(tx.exchangeRate));
        setPaymentDate(dayjs(tx.paymentDate));
        setAmountPaid(String(tx.amountPaid));
        setSourceBankAccount(tx.sourceBankAccount);
        setBankReference(tx.bankReference || "");
        setTrackingKey(tx.trackingKey || "");
      })
      .catch((error) => openSnack(handleError(error), "error"))
      .finally(() => setLoadingDetail(false));
  }, [id, isEdit, openSnack]);

  useEffect(() => {
    if (isEdit) return;
    getVarianceReasonList()
      .then((response) => setVarianceReasons(Array.isArray(response.data) ? response.data : []))
      .catch(() => setVarianceReasons([]));
  }, [isEdit]);

  const handleAllocationReceiptChange = async (index, receipt) => {
    setAllocations((prev) =>
      prev.map((allocation, i) =>
        i === index ? { ...allocation, receipt, installments: [], raPaymentId: "" } : allocation
      )
    );
    if (!receipt) return;
    const response = await getRaPaymentsByReceipt(receipt.id);
    setAllocations((prev) =>
      prev.map((allocation, i) => (i === index ? { ...allocation, installments: response.data } : allocation))
    );
  };

  const handleAllocationChange = (index, field, value) => {
    setAllocations((prev) => prev.map((allocation, i) => (i === index ? { ...allocation, [field]: value } : allocation)));
  };

  const handleAddAllocation = () => setAllocations((prev) => [...prev, emptyAllocation()]);
  const handleRemoveAllocation = (index) => setAllocations((prev) => prev.filter((_, i) => i !== index));

  const appliedTotal = allocations.reduce((sum, a) => sum + (parseFloat(a.amountApplied) || 0), 0);
  const amountPaidNumber = parseFloat(amountPaid) || 0;
  const isOverApplied = appliedTotal > amountPaidNumber + 0.01;

  const isCreateValid = () =>
    provider &&
    currency &&
    paymentDate &&
    amountPaid !== "" &&
    !isNaN(Number(amountPaid)) &&
    exchangeRate !== "" &&
    !isNaN(Number(exchangeRate)) &&
    !isOverApplied &&
    allocations.every((a) => !a.raPaymentId || (a.amountApplied !== "" && !isNaN(Number(a.amountApplied))));

  const handleCreate = async () => {
    if (!isCreateValid() || loading) return;
    try {
      setLoading(true);
      await createPaymentTx({
        paymentTx: {
          companyId,
          providerId: provider.id,
          amountPaid: Number(amountPaid),
          currencyId: currency.id,
          exchangeRate: Number(exchangeRate),
          paymentDate: paymentDate.format("YYYY-MM-DD"),
          sourceBankAccountId: sourceBankAccount?.id || null,
          bankReference: bankReference || null,
          trackingKey: trackingKey || null,
          scheduledPaymentId: scheduledPayment?.id || null,
          reversesPaymentTxId: null,
        },
        allocations: allocations
          .filter((a) => a.raPaymentId)
          .map((a) => ({
            raPaymentId: a.raPaymentId,
            scheduledPaymentId: null,
            amountApplied: Number(a.amountApplied),
            varianceReasonId: a.varianceReasonId || null,
            installmentSeq: null,
            previousBalance: null,
            remainingBalance: null,
          })),
      });
      openSnack("Transacción de pago creada exitosamente", "success");
      if (onSubmit) onSubmit();
    } catch (error) {
      openSnack(handleError(error), "error");
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async () => {
    if (loading) return;
    try {
      setLoading(true);
      await updatePaymentTx(id, {
        sourceBankAccountId: sourceBankAccount?.id || null,
        bankReference: bankReference || null,
        trackingKey: trackingKey || null,
      });
      openSnack("Transacción de pago actualizada exitosamente", "success");
      if (onSubmit) onSubmit();
    } catch (error) {
      openSnack(handleError(error), "error");
    } finally {
      setLoading(false);
    }
  };

  if (loadingDetail) return null;

  return (
    <Box p={1}>
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <LGAutocomplete
            fetchFunction={getProviderList}
            queryParams={{ companyId }}
            optionLabel="code"
            value={provider}
            onChange={(event, value) => setProvider(value)}
            label="Proveedor"
            disabled={isEdit}
          />
        </Grid>
        <Grid item xs={3}>
          <LGAutocomplete
            fetchFunction={getCurrencyList}
            optionLabel="code"
            value={currency}
            onChange={(event, value) => setCurrency(value)}
            label="Moneda"
            disabled={isEdit}
          />
        </Grid>
        <Grid item xs={3}>
          <LGNumber
            fullWidth
            label="Tipo de cambio"
            value={exchangeRate}
            onChange={(e) => setExchangeRate(e.target.value)}
            disabled={isEdit}
          />
        </Grid>
        <Grid item xs={4}>
          <MyDatePicker
            fullWidth
            slotProps={{ textField: { fullWidth: true } }}
            label="Fecha de pago"
            value={paymentDate}
            onChange={(value) => setPaymentDate(value)}
            disabled={isEdit}
          />
        </Grid>
        <Grid item xs={4}>
          <LGNumber
            fullWidth
            label="Monto pagado"
            value={amountPaid}
            onChange={(e) => setAmountPaid(e.target.value)}
            disabled={isEdit}
          />
        </Grid>
        <Grid item xs={4}>
          <LGAutocomplete
            fetchFunction={getCatalogue}
            queryParams={{ CatalogueName: "BANK_ACCOUNT" }}
            optionLabel="name"
            value={sourceBankAccount}
            onChange={(event, value) => setSourceBankAccount(value)}
            label="Cuenta de origen"
          />
        </Grid>
        <Grid item xs={4}>
          <TextField
            fullWidth
            label="Referencia bancaria"
            value={bankReference}
            onChange={(e) => setBankReference(e.target.value)}
          />
        </Grid>
        <Grid item xs={4}>
          <TextField
            fullWidth
            label="Clave de rastreo"
            value={trackingKey}
            onChange={(e) => setTrackingKey(e.target.value)}
          />
        </Grid>
        {!isEdit && (
          <Grid item xs={4}>
            <LGAutocomplete
              fetchFunction={getScheduledPaymentList}
              queryParams={{ companyId, providerId: provider?.id || null }}
              optionLabel="code"
              value={scheduledPayment}
              onChange={(event, value) => setScheduledPayment(value)}
              label="Corrida de pago (opcional)"
            />
          </Grid>
        )}

        {!isEdit && (
          <>
            <Grid item xs={12}>
              <Typography variant="h6">Aplicaciones</Typography>
            </Grid>
            {allocations.map((allocation, index) => (
              <React.Fragment key={index}>
                <Grid item xs={4}>
                  <LGAutocomplete
                    fetchFunction={getReceiptAcknowledgements}
                    fetchBy="code"
                    queryParams={{ companyId, providerCode: provider?.code || null }}
                    optionLabel="code"
                    value={allocation.receipt}
                    onChange={(event, value) => handleAllocationReceiptChange(index, value)}
                    label="Contrarrecibo"
                  />
                </Grid>
                <Grid item xs={3}>
                  <TextField
                    select
                    fullWidth
                    label="Cuota"
                    value={allocation.raPaymentId}
                    disabled={!allocation.receipt}
                    onChange={(e) => handleAllocationChange(index, "raPaymentId", e.target.value)}
                  >
                    {allocation.installments.map((installment) => (
                      <MenuItem key={installment.id} value={installment.id}>
                        Cuota {installment.installmentNo} — {formatCurrency(installment.amount)}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={2}>
                  <LGNumber
                    fullWidth
                    label="Monto aplicado"
                    value={allocation.amountApplied}
                    onChange={(e) => handleAllocationChange(index, "amountApplied", e.target.value)}
                  />
                </Grid>
                <Grid item xs={2}>
                  <TextField
                    select
                    fullWidth
                    label="Razón de variación"
                    value={allocation.varianceReasonId}
                    onChange={(e) => handleAllocationChange(index, "varianceReasonId", e.target.value)}
                  >
                    <MenuItem value="">Ninguna</MenuItem>
                    {varianceReasons.map((reason) => (
                      <MenuItem key={reason.id} value={reason.id}>
                        {reason.name}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={1} display="flex" alignItems="center">
                  <IconButton
                    onClick={() => handleRemoveAllocation(index)}
                    disabled={allocations.length === 1}
                    color="error"
                  >
                    <Icon>delete</Icon>
                  </IconButton>
                </Grid>
              </React.Fragment>
            ))}
            <Grid item xs={12}>
              <Button startIcon={<Icon>add</Icon>} onClick={handleAddAllocation}>
                Agregar aplicación
              </Button>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="body2">
                Aplicado: {formatCurrency(appliedTotal)} / Pagado: {formatCurrency(amountPaidNumber)}
              </Typography>
              {isOverApplied && (
                <Typography variant="body2" color="error">
                  El monto aplicado no puede superar el monto pagado.
                </Typography>
              )}
            </Grid>
          </>
        )}

        <Grid item xs={12}>
          <Stack direction="row" justifyContent="flex-end">
            <Button
              variant="contained"
              color="primary"
              onClick={isEdit ? handleUpdate : handleCreate}
              disabled={loading || (!isEdit && !isCreateValid())}
            >
              {loading ? "Guardando..." : isEdit ? "Guardar cambios" : "Crear transacción de pago"}
            </Button>
          </Stack>
        </Grid>
      </Grid>
    </Box>
  );
}
